/**
 * format — EIN Modul für jede Zahl und jedes Datum, das ein Mensch liest.
 *
 * @description
 *   Wörter englisch (Owner 2026-08-01), Formate deutsch (Owner 2026-10-06).
 *   `LOCALE` ist FEST `de-DE` — nie `undefined`. Ein Aufruf ohne Locale folgt
 *   der Browser-Einstellung, und genau daraus entstand die Mischung aus vier
 *   Datumsformen und einem halben Dutzend Geldformaten (§1.1 des Plans).
 *
 *   Datum und Zeit werden NICHT hier gebaut, sondern aus `./time` durchgereicht:
 *   dort steht der shape-aware Parser (offsetlose Backend-Strings als UTC, bare
 *   `YYYY-MM-DD` als Kalendertag) und die feste Zeitzone Europe/Berlin. Zwei
 *   Datums-Motoren nebeneinander wären genau der Fehler, den dieses Modul beendet.
 *
 *   🔴 **F11 (UI-Durchgang 2, 2026-10-07): EIN Datumsformat.** Sichtbar wird
 *   `dd.MM.yyyy` (führende Null, vierstelliges Jahr), mit Uhrzeit
 *   `dd.MM.yyyy, HH:mm` — über `formatDate` / `formatDateTime` / `formatTime`.
 *   Ein `toLocaleDateString`/`toLocaleString` **ohne Optionen** oder mit
 *   `day: 'numeric'` liefert „2.10.2026" bzw. die Browser-Form und ist genau die
 *   Mischung, die dieses Modul beendet. Ausnahmen bleiben benannt:
 *   `formatShortDate` (`dd.MM.`) für enge Zellen, `withSeconds` für Protokolle.
 *
 *   ⚠️ Kopf in Regel-12-Form, aber `format.js` ist ein **Util**, kein Bauteil —
 *   der Eintrag unter „Shared Utils" in `component-registry.md` ist deshalb
 *   **keine** Regel-12-Pflicht, sondern eine dokumentierte Ergänzung (Grill W6).
 *
 * @useWhen
 *   - Ein Betrag, eine Zahl, ein Prozentwert, eine Dateigröße oder ein Datum soll sichtbar werden
 *   - Ein Wert soll deutsch formatiert sein, unabhängig von der Browser-Einstellung
 *
 * @dontUseFor
 *   - Maschinenwerte: Payload, `datetime`-Attribut, ISO-String, CSV-Export, px-Breite
 *     (dafür `berlinDateISO`, `String(value)` bzw. eine Rundung auf eine Zahl)
 *   - Kundentext (PDF, Mail, Shop) — der folgt seinem eigenen Format (Regel 20 §2;
 *     die konkrete Kollisionsstelle ist `components/common/SmartCompose/macros.js`)
 *   - Eine Rundung auf eine ZAHL (`Number(x.toFixed(2))` — Rechnung, kein Anzeigewert;
 *     siehe `utils/fxUtils.js`)
 *
 * @related
 *   - utils/time.js — der Datums-Motor; format.js reicht ihn durch
 *   - utils/parseMoney.js — die Gegenrichtung (String → Zahl)
 *
 * @reuseHints
 *   - Der Wächter `scripts/check_format_calls.py` hält neue locale-lose Aufrufe
 *     (`toFixed(`, argumentloses `toLocaleString()`, `Intl.*` ohne Locale) auf —
 *     **und** seit dem 06.10. ein BLANKES Locale-**Literal** ≠ `de-DE`, aber nur
 *     bei `toLocale{String,DateString,TimeString}` und
 *     `Intl.{NumberFormat,DateTimeFormat}` (`Intl.NumberFormat('en-GB', …)`
 *     setzte `€1,234.56`); ein `// format-guard: ok` befreit eine bewusst
 *     gewählte Stelle. 🔴 NICHT gedeckt — `Intl.DisplayNames`/`RelativeTimeFormat`/
 *     `Collator`, `localeCompare`, `toLocaleUpperCase`/`toLocaleLowerCase`, ein
 *     Locale aus einer Konstante und die `?.()`-Kurzform; wer dort ein englisches
 *     Literal setzt, ist ungemeldet
 */

import {
  parseTimestamp,
  formatBerlinDate,
  formatBerlinDateTime,
  formatBerlinTime,
  formatBerlinShortDate,
  formatBerlinRelative,
  berlinDateISO,
} from './time'

export const LOCALE = 'de-DE'
export const TZ = 'Europe/Berlin'

/** Die eine Locale für die englischen NAMEN (Monat, Wochentag) — fest, nie die des Browsers. */
const EN_LOCALE = 'en-US'

export {
  // Datum/Zeit — durchgereicht aus `./time`, damit es EINEN Motor gibt.
  parseTimestamp,
  formatBerlinDate as formatDate,
  formatBerlinDateTime as formatDateTime,
  formatBerlinTime as formatTime,
  formatBerlinShortDate as formatShortDate,
  formatBerlinRelative as formatRelative,
  berlinDateISO,
}

/**
 * Coerce a value to a finite number, or `null` when it is missing/unusable.
 *
 * 🔴 "Unusable" is wider than "empty" (Regel 22 §3 — unknown is never `0`):
 * `Number('')`, `Number(' ')` and `Number(false)` are all `0`, so an absent
 * field would render a confident `0,00 €`. Whitespace-only strings and booleans
 * are therefore `null`, not `0`. Numeric strings are accepted (`'12.5'`)
 * because payloads carry them.
 */
function _finite(value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return null
  if (typeof value === 'string' && value.trim() === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * One `Intl.NumberFormat` per option set, cached.
 *
 * This module is called in render loops; building a formatter per call is the
 * single most expensive thing it could do. The cache key is the option object
 * serialised — the shapes below are fixed literals, so the key is stable.
 */
const _formatters = new Map()
function _fmt(options) {
  const key = JSON.stringify(options)
  let f = _formatters.get(key)
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, options)
    _formatters.set(key, f)
  }
  return f
}

/**
 * `Intl.DateTimeFormat` for the English NAMES, cached — same reason as `_fmt`.
 *
 * 🔴 The locale is a literal (`en-US`), not the browser's. An English month
 * name that follows the browser setting would render `Okt 2026` on a German
 * machine and `Oct 2026` on an English one — the exact mixture this module
 * exists to remove. Every option set here also pins `timeZone: 'UTC'`; the
 * date handed in is already the Berlin calendar day, and formatting it in
 * local time would shift it back a day for anything west of Berlin.
 */
const _formattersEn = new Map()
function _fmtEn(options) {
  const key = JSON.stringify(options)
  let f = _formattersEn.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(EN_LOCALE, options)
    _formattersEn.set(key, f)
  }
  return f
}

/**
 * The Berlin calendar day of an input as a UTC-anchored `Date`, or `null`.
 *
 * Used only by the English NAME formatters: they need a `Date` whose
 * year/month/day ARE the Berlin ones, so that a `timeZone: 'UTC'` formatter
 * prints exactly those. `berlinDateISO` already does the Berlin resolution
 * (including the 22:00–24:00 UTC band), so this re-derives the `Date` from its
 * `YYYY-MM-DD` string rather than duplicating the timezone logic.
 */
function _berlinDayUTC(input) {
  const iso = berlinDateISO(input)
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

/** Non-breaking space — between a value and its unit (€, %, KB), German house style. */
const NBSP = ' '

/**
 * Geld. `1234.5` → `1.234,50 €` (NBSP vor dem €).
 *
 * 🔴 `currencyDisplay` hängt an der Währung: **EUR** trägt das Symbol `€`,
 * **jede Fremdwährung** den Code HINTEN (`1.234,50 USD`), nie `$` vorne — der
 * Owner-Entscheid vom 06.10. nennt genau diese Form. `digits: 0` erzeugt die
 * bewusste kompakte Anzeige (`6.651 €`, Trennzeichen bleibt).
 *
 * @param {number|string|null|undefined} value
 * @param {{currency?: string, digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatMoney(value, { currency = 'EUR', digits = 2, placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return _fmt({
    style: 'currency',
    currency,
    currencyDisplay: currency === 'EUR' ? 'symbol' : 'code',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n)
}

/**
 * Dezimalzahl mit Tausendertrennzeichen. `1234.561` → `1.234,56`.
 *
 * @param {number|string|null|undefined} value
 * @param {{digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatNumber(value, { digits = 2, placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return _fmt({ minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)
}

/**
 * Ganze Zahl mit Tausendertrennzeichen, gerundet. `1234.6` → `1.235`.
 *
 * @param {number|string|null|undefined} value
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatInt(value, { placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return _fmt({ minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

/**
 * Stückzahl — wie `formatInt`, aber als eigene Absicht: eine Menge hat keine
 * Nachkommastellen. `30` → `30`, `1234` → `1.234`. Der Unterschied zu
 * `formatInt` ist die Semantik an der Aufrufstelle, nicht die Ausgabe.
 *
 * @param {number|string|null|undefined} value
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatQty(value, { placeholder = '—' } = {}) {
  return formatInt(value, { placeholder })
}

/**
 * Prozent. 🔴 Der Eingang ist ein **Bruch**, nicht ein Prozentwert:
 * `0.666` → `66,6 %`, NICHT `0,7 %`. Bestehender Code übergibt teils schon
 * multiplizierte Werte (`(x * 100).toFixed(0)`) — die Umstellung rechnet sie
 * auf `formatPercent(x, { digits: 0 })` zurück, sonst verschiebt sich jede
 * Zahl um zwei Stellen (Grill W9).
 *
 * @param {number|string|null|undefined} value Bruch, z. B. `0.666`
 * @param {{digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatPercent(value, { digits = 1, placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return _fmt({
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n)
}

/**
 * Zähler, der als Näherung gelesen wird. `152497` → `~152.497`.
 * Die Tilde ist Teil der Aussage („ungefähr"), nicht Kosmetik.
 *
 * @param {number|string|null|undefined} value
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatTokens(value, { placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return `~${_fmt({ minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)}`
}

/** Binary units (1024), German house style — `24 KB`, not `24.0 KB`. */
const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

/**
 * Dateigröße. `24576` → `24 KB`, `1572864` → `1,5 MB`.
 *
 * `digits` ist die OBERE Grenze der Nachkommastellen, nicht eine feste Zahl:
 * `24 KB` soll nicht als `24,0 KB` erscheinen, `1,5 MB` aber die 5 behalten.
 * Daher `minimumFractionDigits: 0` — Bytes selbst (`i === 0`) tragen gar keine.
 *
 * @param {number|string|null|undefined} bytes
 * @param {{digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatBytes(bytes, { digits = 1, placeholder = '—' } = {}) {
  const n = _finite(bytes)
  if (n === null) return placeholder
  let v = n
  let i = 0
  while (Math.abs(v) >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024
    i += 1
  }
  const max = i === 0 ? 0 : digits
  const num = _fmt({ minimumFractionDigits: 0, maximumFractionDigits: max }).format(v)
  return `${num}${NBSP}${BYTE_UNITS[i]}`
}

/**
 * Dauer. `1.5` → `1,5 s`, `4320` → `1,2 h`, `90` → `1 m 30 s`.
 *
 * 🔴 Drei Stufen, nicht zwei: unter einer Minute Sekunden, unter einer Stunde
 * Minuten+Sekunden, darüber Stunden. Die mittlere Stufe fehlt in der
 * Plan-Beschreibung („unter 60 s Sekunden, darüber Stunden"), steht aber im
 * Bestand dreifach kopiert (`JobDetail`/`JobsList`/`JobCard`) — liesse man sie
 * weg, würde `90` zu `0,0 h`. Sie wird hier wortgetreu nachgebaut.
 *
 * @param {number|string|null|undefined} seconds
 * @param {{digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatDuration(seconds, { digits = 1, placeholder = '—' } = {}) {
  const n = _finite(seconds)
  if (n === null) return placeholder
  const abs = Math.abs(n)
  if (abs < 60) return `${formatNumber(n, { digits, placeholder })}${NBSP}s`
  if (abs < 3600) {
    const m = Math.floor(abs / 60)
    const s = Math.round(abs % 60)
    return `${formatInt(m)}${NBSP}m ${formatInt(s)}${NBSP}s`
  }
  return `${formatNumber(n / 3600, { digits, placeholder })}${NBSP}h`
}

/**
 * Zahl mit Einheit. `24.5, 'kg'` → `24,5 kg` (NBSP vor der Einheit, Hausform
 * wie bei `formatMoney`/`formatBytes`). `digits` kommt von der Aufrufstelle —
 * `kg`/`d` tragen eine Nachkommastelle, `m³` zwei.
 *
 * @param {number|string|null|undefined} value
 * @param {string} unit
 * @param {{digits?: number, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatUnit(value, unit, { digits = 2, placeholder = '—' } = {}) {
  const n = _finite(value)
  if (n === null) return placeholder
  return `${formatNumber(n, { digits, placeholder })}${NBSP}${unit}`
}

/**
 * Zahl mit Einheit, aber NUR wenn es die Zahl gibt: `21, 'd'` → `21d`; fehlt der
 * Wert, steht der Ersatzstrich ALLEIN — nie `—d`.
 *
 * 🔴 Anlass (UI-Durchgang 2, 2026-10-07): sechs Flaechen haengten die Einheit
 *    an den Ersatzstrich (`{v ?? '—'}d`). „—d" liest sich wie ein Wert
 *    („Strich-Tage"); die Einheit gehoert an den WERT, nicht an den Platzhalter.
 *
 * 🔴 Zwei Unterschiede zu `formatUnit` — und beide sind Absicht:
 *    - **kein NBSP** und **keine aufgezwungenen Nachkommastellen**: diese
 *      Aufrufer sind enge Listen- und Hinweiszeilen (`21d`, nicht `21,0 d`).
 *    - **`0` ist ein WERT.** `0d` bleibt `0d` („same day" ist eine Aussage).
 *      Fehlend heisst `null`, `undefined` oder der leere String — `''` kommt
 *      wirklich vor (`VendorDetails.anzeigename.test.jsx` baut den Lieferanten
 *      so), und `??` faengt ihn NICHT.
 *
 * 🔴 Der Bruchwert traegt das DEZIMALKOMA, nicht den Punkt (UI-Durchgang 2,
 *    2026-10-07, Regel-16-Befund W4): `3.5` → `3,5d`. Bis dahin hing der Wert
 *    roh am String, und `3.5d` widersprach in einer `de-DE`-App jedem Nachbarn
 *    (`formatNumber`/`formatUnit`/`formatInt`). Die Zahl laeuft deshalb durch
 *    DIESELBE Formatierung wie die Nachbarn — Trennzeichen inklusive —, nur mit
 *    `minimumFractionDigits: 0`: ein ganzzahliger Zaehler bleibt `21d`.
 *    Nicht-Numerisches zaehlt wie ein fehlender Wert (`—`), genau wie bei
 *    `formatUnit`, und alle heutigen Aufrufer liefern Zahlen (`lead_time` ist
 *    `db.Integer`). 🔴 KEINE Anzahl hinschreiben — sie altert (R15 W-B.1,
 *    2026-10-07): die Aufrufer findet man mit
 *    `grep -rn "formatUnitCompact" frontend/src --include='*.jsx' --include='*.js'`.
 *
 * 🔴 Und die Gruppierung ist GEWOLLT (R15 W-B.2, 2026-10-07): `_fmt` schaltet
 *    `useGrouping` nicht ab, `1500` wird also `1.500d` (Locale `de-DE`). Das ist
 *    dieselbe Form wie bei `formatNumber` und den uebrigen Nachbarn; die Probe
 *    `format.test.js` pinnt sie.
 *
 * @param {number|string|null|undefined} value
 * @param {string} [unit='d']
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatUnitCompact(value, unit = 'd', { placeholder = '—' } = {}) {
  if (value === null || value === undefined || value === '') return placeholder
  const n = _finite(value)
  if (n === null) return placeholder
  return `${_fmt({ minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}${unit}`
}

/**
 * Kalendermonat numerisch, Berliner Monat: `10/2026`.
 *
 * 🔴 Der Owner-Entscheid nennt genau diese Form für **Diagrammachsen**. Im
 * Bestand hat sie heute **keinen** Anwender (§2: die eine Zeitachse ist
 * TAGESgranular, alle Monatsstellen sind Etiketten) — sie wird gebaut, damit
 * die nächste Monatsachse sie vorfindet, statt neu erfunden zu werden.
 *
 * @param {Date|string|number|null|undefined} input
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatMonthYear(input, { placeholder = '—' } = {}) {
  const iso = berlinDateISO(input)
  if (!iso) return placeholder
  const [y, m] = iso.split('-')
  return `${m}/${y}`
}

/**
 * Monatsname + Jahr, ENGLISCH: `Oct 2026` (Owner-Form; die Wörter sind
 * englisch, die Zahl ist es nicht).
 *
 * @param {Date|string|number|null|undefined} input
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatMonthNameYear(input, { placeholder = '—' } = {}) {
  const d = _berlinDayUTC(input)
  if (!d) return placeholder
  return _fmtEn({ month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d)
}

/**
 * Monat + Tag, ENGLISCH: `Oct 6`. Die Schwester von `formatMonthNameYear` ohne
 * Jahr — die Form der Wochen-Bucket-Beschriftung.
 *
 * @param {Date|string|number|null|undefined} input
 * @param {{placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatMonthDay(input, { placeholder = '—' } = {}) {
  const d = _berlinDayUTC(input)
  if (!d) return placeholder
  return _fmtEn({ month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d)
}

/**
 * Wochentag, ENGLISCH: `Mon` / mit `long: true` `Monday`.
 *
 * @param {Date|string|number|null|undefined} input
 * @param {{long?: boolean, placeholder?: string}} [opts]
 * @returns {string}
 */
export function formatWeekday(input, { long = false, placeholder = '—' } = {}) {
  const d = _berlinDayUTC(input)
  if (!d) return placeholder
  return _fmtEn({ weekday: long ? 'long' : 'short', timeZone: 'UTC' }).format(d)
}
